import { useCallback, useContext, useEffect, useState } from "react";
import { SurveyBlocks } from "../surveyBlocks";
import {
  DefaultMaxColumns,
  DefaultMaxRows,
  deleteFormattingInstructions,
  FormattingRuleType,
  getMaxRowsAndColumns,
  getNextEmptyStartEndRowCol,
  isPositionInstructionsHasMissingEntities,
  PositionInstructions,
  positionToStartEndRowCol,
  startEndRowColToCellPosition,
  updatePositionInstructions,
} from "@coolset/cs-ui";
import { FormattingRule, ReorderIndexing, updateMultipleCellPositions } from "@coolset/cs-ui";
import { BlockFormValuesStore } from "./useBlockFormValues";
import { LayoutContext } from "contexts/LayoutContext/LayoutContext";

export const usePositioningTable = (blockFormValuesStore: BlockFormValuesStore, tableBlocks: SurveyBlocks.Block[]) => {
  const { blockValues, setBlockValues, updateBlockApi, isSaving } = blockFormValuesStore;
  const { positionInstructions, formattingInstructions } = blockValues;
  const { genericError } = useContext(LayoutContext);

  const [isAwaitingBlockUpdate, setIsAwaitingBlockUpdate] = useState<boolean>(false);

  const setTableInstructions = useCallback(
    async (positionInstructions: PositionInstructions, formattingInstructions?: FormattingRule[]) => {
      const { maxRows, maxColumns } = getMaxRowsAndColumns(positionInstructions);
      const lastBlockValues = { ...blockValues };
      setBlockValues((values) => ({
        ...values,
        positionInstructions,
        formattingInstructions: formattingInstructions ?? blockValues.formattingInstructions,
        maxRows: Math.max(maxRows, blockValues.maxRows ?? DefaultMaxRows),
        maxColumns: Math.max(maxColumns, blockValues.maxColumns ?? DefaultMaxColumns),
      }));

      try {
        await updateBlockApi(true);
      } catch (error) {
        console.error(error);
        setBlockValues(lastBlockValues);
      }
    },
    [blockValues, setBlockValues, updateBlockApi]
  );

  const handleRepositionBlocks = useCallback(
    async (reorderIndexing: ReorderIndexing) => {
      if (!positionInstructions || !formattingInstructions) return;

      const [newFormattingInstructions, error] = updateMultipleCellPositions(formattingInstructions, reorderIndexing);
      if (error) {
        genericError(error);
        return;
      }

      const newPositionInstructions = updatePositionInstructions(positionInstructions, reorderIndexing);

      await setTableInstructions(newPositionInstructions, newFormattingInstructions);
      setIsAwaitingBlockUpdate(false);
    },
    [formattingInstructions, genericError, positionInstructions, setTableInstructions]
  );

  const addBlockPosition = useCallback(
    async (block: SurveyBlocks.Block, position: string) => {
      const newPositionInstructions = { ...positionInstructions, [block.id]: positionToStartEndRowCol(position) };

      await setTableInstructions(newPositionInstructions);
      setIsAwaitingBlockUpdate(false);
    },
    [positionInstructions, setTableInstructions]
  );

  const populatePositionInstructionsWithUnorderedBlocks = useCallback(async () => {
    const newPositionInstructions = { ...positionInstructions };
    tableBlocks.forEach((block, i) => {
      newPositionInstructions[block.id] =
        newPositionInstructions[block.id] ?? getNextEmptyStartEndRowCol(positionInstructions!, i);
    });

    await setTableInstructions(newPositionInstructions);
    setIsAwaitingBlockUpdate(false);

    genericError("found unordered blocks, and placed them at the end of the table");
  }, [positionInstructions, tableBlocks, setTableInstructions, genericError]);

  const handleAfterDeleteBlock = useCallback(
    async (blockId: string) => {
      const newPositionInstructions = { ...positionInstructions };
      const blockPosition = startEndRowColToCellPosition(newPositionInstructions[blockId]);

      delete newPositionInstructions[blockId];
      const [newFormattingInstructions] = deleteFormattingInstructions(
        formattingInstructions,
        undefined,
        (rule) => rule.ruleType !== FormattingRuleType.Cell || rule.position !== blockPosition
      );

      await setTableInstructions(newPositionInstructions, newFormattingInstructions);
      setIsAwaitingBlockUpdate(false);
    },
    [formattingInstructions, positionInstructions, setTableInstructions]
  );

  useEffect(() => {
    if (isSaving || isAwaitingBlockUpdate || !positionInstructions || !tableBlocks.length) return;

    if (isPositionInstructionsHasMissingEntities(tableBlocks, positionInstructions)) {
      setIsAwaitingBlockUpdate(true);
      populatePositionInstructionsWithUnorderedBlocks();
    }
  }, [
    positionInstructions,
    tableBlocks,
    setTableInstructions,
    isSaving,
    populatePositionInstructionsWithUnorderedBlocks,
    isAwaitingBlockUpdate,
  ]);

  return {
    positionInstructions: positionInstructions || {},
    handleRepositionBlocks,
    setIsAwaitingBlockUpdate,
    addBlockPosition,
    handleAfterDeleteBlock,
  };
};

export type PositionInstructionsStore = ReturnType<typeof usePositioningTable>;
